/**
 * Ground-truth ambient occlusion (Jimenez et al. 2016 horizon integral),
 * half resolution, blue-noise dithered, separable bilateral denoise, then a
 * depth-aware bilateral resolve to full res — plus, fused into that same
 * full-res pass, a screen-space contact shadow.
 *
 * The output is `vec4(ao, contact, 0, 1)` in one RGBA8 texture:
 *
 *   .r  ambient occlusion  -> multiplies INDIRECT diffuse in the forward pass
 *   .g  contact shadow     -> multiplies DIRECT light in the forward pass
 *
 * Packing them together is not tidiness. The forward pass runs out of texture
 * image units before it runs out of anything else (FINDINGS F6: 16 units, the
 * shadow atlas and env map eat most of them, materials had to drop to 3
 * samplers), so a second sampler on every scene material is a real cost and a
 * spare byte in an existing one is free.
 *
 * Why a contact shadow exists at all: the sun cascades cannot resolve occlusion
 * below one shadow texel, and that texel is ~2 cm in the nearest cascade and
 * ~24 cm in the furthest. Everything shorter than that — a strap against a
 * plate carrier, an optic above a receiver, a boot meeting rock — has no shadow
 * at all. This pass marches the depth buffer toward the sun over 0.2-0.5 m and
 * fills in exactly that band.
 *
 * owner: render
 */
import * as THREE from 'three';
import { Blitter, makeMaterial, makeRT, GLSL_COMMON } from './Common.js';

const GTAO_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}

uniform sampler2D tDepth;
uniform sampler2D tNormal;
uniform sampler2D tNoise;
uniform mat4 uInvProj;
uniform vec2 uTexel;
uniform vec2 uResolution;
uniform vec2 uNoiseScale;
uniform float uRadius;
uniform float uIntensity;
uniform float uPower;
uniform float uProj11;
uniform float uFrame;

varying vec2 vUv;

void main() {
  float d = texture2D(tDepth, vUv).x;
  if (d >= 0.99999) { gl_FragColor = vec4(1.0, 1e4, 0.0, 1.0); return; }

  vec3 P = viewFromDepth(vUv, d, uInvProj);
  float viewZ = max(-P.z, 1e-3);
  vec3 N = normalize(texture2D(tNormal, vUv).xyz);
  vec3 V = normalize(-P);

  vec4 bn = texture2D(tNoise, vUv * uNoiseScale);
  float rotOff = fract(bn.r + uFrame * 0.6180339887498949);
  float stepOff = fract(bn.g + uFrame * 0.4142135623730951);

  float radiusPix = uRadius * uProj11 * 0.5 * uResolution.y / viewZ;
  radiusPix = clamp(radiusPix, 3.0, 88.0);

  float falloffStart = uRadius * 0.7;
  float falloffRange = max(uRadius * 0.35, 1e-4);
  const float bias = 0.035;

  float visibility = 0.0;

  for (int s = 0; s < PF_SLICES; s++) {
    float phi = (float(s) + rotOff) * (PI_ / float(PF_SLICES));
    vec2 omega = vec2(cos(phi), sin(phi));
    vec3 dirVS = vec3(omega, 0.0);

    vec3 axis = cross(dirVS, V);
    float axisLen = length(axis);
    if (axisLen < 1e-5) continue;
    axis /= axisLen;

    vec3 projN = N - axis * dot(N, axis);
    float projLen = length(projN);
    if (projLen < 1e-4) continue;
    vec3 pn = projN / projLen;

    vec3 T = normalize(cross(V, axis));
    float n = atan(dot(pn, T), dot(pn, V));

    float cosHp = -1.0;
    float cosHm = -1.0;

    for (int t = 0; t < PF_STEPS; t++) {
      float f = (float(t) + stepOff) / float(PF_STEPS);
      float r = max(f * f * radiusPix, 1.5);
      vec2 off = omega * r * uTexel;

      vec2 uvp = vUv + off;
      vec2 uvm = vUv - off;

      float dp = texture2D(tDepth, uvp).x;
      float dm = texture2D(tDepth, uvm).x;

      if (dp < 0.99999) {
        vec3 sp = viewFromDepth(uvp, dp, uInvProj);
        vec3 dv = sp - P;
        float l = length(dv);
        float c = dot(dv, V) / max(l, 1e-5) - bias;
        c = mix(c, -1.0, saturate((l - falloffStart) / falloffRange));
        cosHp = max(cosHp, c);
      }
      if (dm < 0.99999) {
        vec3 sp = viewFromDepth(uvm, dm, uInvProj);
        vec3 dv = sp - P;
        float l = length(dv);
        float c = dot(dv, V) / max(l, 1e-5) - bias;
        c = mix(c, -1.0, saturate((l - falloffStart) / falloffRange));
        cosHm = max(cosHm, c);
      }
    }

    float h1 = acos(clamp(cosHp, -1.0, 1.0));
    float h0 = -acos(clamp(cosHm, -1.0, 1.0));
    h1 = n + min(h1 - n, HALF_PI_);
    h0 = n + max(h0 - n, -HALF_PI_);

    float sn = sin(n), cn = cos(n);
    float vd = 0.25 * (-cos(2.0 * h0 - n) + cn + 2.0 * h0 * sn)
             + 0.25 * (-cos(2.0 * h1 - n) + cn + 2.0 * h1 * sn);
    visibility += projLen * vd;
  }

  visibility = saturate(visibility / float(PF_SLICES));
  float ao = mix(1.0, pow(visibility, uPower), uIntensity);
  gl_FragColor = vec4(ao, viewZ, 0.0, 1.0);
}
`;

const BLUR_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tAO;
uniform vec2 uDir;      // texel-sized step
uniform float uDepthSigma;
varying vec2 vUv;

void main() {
  vec2 c = texture2D(tAO, vUv).xy;
  float centerZ = c.y;
  float sum = c.x * 0.2270270270;
  float wsum = 0.2270270270;
  const float off[4] = float[4](1.0, 2.0, 3.0, 4.0);
  const float wgt[4] = float[4](0.1945945946, 0.1216216216, 0.0540540541, 0.0162162162);
  for (int i = 0; i < 4; i++) {
    vec2 d = uDir * off[i];
    vec2 a = texture2D(tAO, vUv + d).xy;
    vec2 b = texture2D(tAO, vUv - d).xy;
    float wa = wgt[i] * exp(-abs(a.y - centerZ) / (uDepthSigma * centerZ + 1e-3));
    float wb = wgt[i] * exp(-abs(b.y - centerZ) / (uDepthSigma * centerZ + 1e-3));
    sum += a.x * wa + b.x * wb;
    wsum += wa + wb;
  }
  gl_FragColor = vec4(sum / max(wsum, 1e-5), centerZ, 0.0, 1.0);
}
`;

const RESOLVE_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tAO;      // half res: r = ao, g = viewZ
uniform sampler2D tDepth;   // full res hardware depth
uniform sampler2D tNormal;  // full res view-space normal
uniform sampler2D tNoise;
uniform mat4 uInvProj;
uniform mat4 uProj;
uniform vec2 uHalfTexel;
uniform vec2 uNoiseScale;
uniform float uAoEnabled;
uniform float uCsStrength;    // 0 disables the march entirely
uniform vec3 uLightVS;        // view-space unit vector TOWARD the light
uniform float uCsLength;      // ray length in metres
uniform float uCsThickness;   // how deep a hit still counts as an occluder, metres
uniform float uCsBias;        // metres
uniform float uCsFadeStart;   // view distance where the effect starts fading out
uniform float uCsFadeEnd;
uniform float uFrame;
varying vec2 vUv;

/**
 * March the depth buffer toward the light. A hit inside uCsThickness is an
 * occluder; hits close to the shading point bite hardest, which is what turns
 * this into contact *hardening* rather than a second flat shadow term.
 *
 * The ray is short on purpose. Long screen-space shadow rays produce the two
 * classic artefacts — a dark halo where the occluder leaves the screen, and a
 * black smear behind thin geometry whose back face the depth buffer cannot see.
 * At 0.2-0.5 m neither has room to appear, and beyond that the sun cascade is
 * already doing the job.
 */
float contactShadow(vec3 P, vec3 N, float jitter) {
  float ndl = dot(N, uLightVS);
  if (ndl <= 0.03) return 1.0;              // the BRDF already has this pixel dark

  float viewZ = -P.z;
  float fade = 1.0 - saturate((viewZ - uCsFadeStart) / max(uCsFadeEnd - uCsFadeStart, 1e-3));
  if (fade <= 0.002) return 1.0;

  /* Leave the surface before the first tap: along the normal (scaled up at
     grazing incidence, where a depth texel spans the most world space) and a
     little along the ray, or every pixel shadows itself. */
  vec3 origin = P + N * (uCsBias * (1.0 + 3.0 * (1.0 - ndl))) + uLightVS * uCsBias;

  float stepLen = uCsLength / float(PF_CS_STEPS);
  float occ = 0.0;
  for (int i = 0; i < PF_CS_STEPS; i++) {
    float t = (float(i) + jitter) * stepLen;
    vec3 S = origin + uLightVS * t;
    vec4 clip = uProj * vec4(S, 1.0);
    if (clip.w <= 1e-5) break;
    vec2 suv = clip.xy / clip.w * 0.5 + 0.5;
    if (suv.x < 0.0 || suv.x > 1.0 || suv.y < 0.0 || suv.y > 1.0) break;
    float sd = texture2D(tDepth, suv).x;
    if (sd >= 0.99999) continue;            // sky: nothing there to occlude with
    float sceneZ = -viewFromDepth(suv, sd, uInvProj).z;
    float diff = (-S.z) - sceneZ;           // > 0: the ray is behind real geometry
    if (diff > uCsBias && diff < uCsThickness) {
      float w = 1.0 - t / uCsLength;
      occ = max(occ, w * w);
    }
  }
  return 1.0 - saturate(occ * uCsStrength * fade);
}

void main() {
  float d = texture2D(tDepth, vUv).x;
  if (d >= 0.99999) { gl_FragColor = vec4(1.0); return; }
  vec3 P = viewFromDepth(vUv, d, uInvProj);
  float z = -P.z;

  float ao = 1.0;
  if (uAoEnabled > 0.5) {
    float sum = 0.0, wsum = 0.0;
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 uv = vUv + vec2(float(x), float(y)) * uHalfTexel;
        vec2 s = texture2D(tAO, uv).xy;
        float w = exp(-abs(s.y - z) * 8.0 / max(z, 0.1));
        w *= (x == 0 && y == 0) ? 1.0 : 0.6;
        sum += s.x * w;
        wsum += w;
      }
    }
    ao = wsum > 1e-5 ? sum / wsum : 1.0;
  }

  float cs = 1.0;
  if (uCsStrength > 0.0) {
    vec3 N = normalize(texture2D(tNormal, vUv).xyz);
    float jitter = fract(texture2D(tNoise, vUv * uNoiseScale).g + uFrame * 0.6180339887498949);
    cs = contactShadow(P, N, jitter);
  }

  gl_FragColor = vec4(ao, cs, 0.0, 1.0);
}
`;

export class GTAO {
  constructor(blitter) {
    this.blit = blitter || new Blitter();
    this.halfRT = null;
    this.blurRT = null;
    this.fullRT = null;
    this.vmRT = null;
    this.w = 0; this.h = 0;

    this.matAO = makeMaterial({
      name: 'postfx.gtao',
      fragmentShader: GTAO_FRAG,
      defines: { PF_SLICES: 3, PF_STEPS: 6 },
      uniforms: {
        tDepth: { value: null }, tNormal: { value: null }, tNoise: { value: null },
        uInvProj: { value: new THREE.Matrix4() },
        uTexel: { value: new THREE.Vector2() },
        uResolution: { value: new THREE.Vector2() },
        uNoiseScale: { value: new THREE.Vector2() },
        uRadius: { value: 1.1 },
        uIntensity: { value: 1.0 },
        uPower: { value: 1.6 },
        uProj11: { value: 1.0 },
        uFrame: { value: 0 },
      },
    });

    this.matBlur = makeMaterial({
      name: 'postfx.gtao.blur',
      fragmentShader: BLUR_FRAG,
      uniforms: {
        tAO: { value: null },
        uDir: { value: new THREE.Vector2() },
        uDepthSigma: { value: 0.035 },
      },
    });

    this.matUp = makeMaterial({
      name: 'postfx.gtao.resolve',
      fragmentShader: RESOLVE_FRAG,
      defines: { PF_CS_STEPS: 12 },
      uniforms: {
        tAO: { value: null }, tDepth: { value: null },
        tNormal: { value: null }, tNoise: { value: null },
        uInvProj: { value: new THREE.Matrix4() },
        uProj: { value: new THREE.Matrix4() },
        uHalfTexel: { value: new THREE.Vector2() },
        uNoiseScale: { value: new THREE.Vector2() },
        uAoEnabled: { value: 1 },
        uCsStrength: { value: 0 },
        uLightVS: { value: new THREE.Vector3(0, 1, 0) },
        uCsLength: { value: 0.35 },
        uCsThickness: { value: 0.55 },
        uCsBias: { value: 0.012 },
        uCsFadeStart: { value: 22 },
        uCsFadeEnd: { value: 45 },
        uFrame: { value: 0 },
      },
    });
  }

  setQuality(slices, steps, csSteps) {
    if (this.matAO.defines.PF_SLICES !== slices || this.matAO.defines.PF_STEPS !== steps) {
      this.matAO.defines.PF_SLICES = slices;
      this.matAO.defines.PF_STEPS = steps;
      this.matAO.needsUpdate = true;
    }
    if (csSteps !== undefined && this.matUp.defines.PF_CS_STEPS !== csSteps) {
      this.matUp.defines.PF_CS_STEPS = csSteps;
      this.matUp.needsUpdate = true;
    }
  }

  setSize(w, h) {
    if (this.w === w && this.h === h) return;
    this.w = w; this.h = h;
    const hw = Math.max(1, w >> 1), hh = Math.max(1, h >> 1);
    for (const rt of [this.halfRT, this.blurRT, this.fullRT, this.vmRT]) if (rt) rt.dispose();
    this.halfRT = makeRT(hw, hh, { name: 'postfx.ao.half' });
    this.blurRT = makeRT(hw, hh, { name: 'postfx.ao.blur' });
    this.fullRT = makeRT(w, h, { type: THREE.UnsignedByteType, name: 'postfx.ao.full' });
    /* The viewmodel is shaded after the world, off its own G-buffer, so it needs
       its own occlusion buffer. It is nearly free: the depth buffer at that
       point contains only the viewmodel, so ~90 % of the frame takes the sky
       early-out in both the AO kernel and the contact march. */
    this.vmRT = makeRT(w, h, { type: THREE.UnsignedByteType, name: 'postfx.ao.viewmodel' });
  }

  get texture() { return this.fullRT.texture; }
  get viewmodelTexture() { return this.vmRT.texture; }

  /**
   * @param {object} p
   * @param {boolean} [p.ao]      run the horizon-integral AO stages (default true)
   * @param {object|null} [p.contact]  { lightVS, strength, length, thickness, bias, fadeStart, fadeEnd }
   * @param {THREE.WebGLRenderTarget} [target]  defaults to the world buffer
   */
  render(renderer, p, target) {
    const hw = this.halfRT.width, hh = this.halfRT.height;
    const noiseSize = p.noiseSize || 64;
    const doAO = p.ao !== false;

    if (doAO) {
      const u = this.matAO.uniforms;
      u.tDepth.value = p.depthTexture;
      u.tNormal.value = p.normalTexture;
      u.tNoise.value = p.noiseTexture;
      u.uInvProj.value.copy(p.invProj);
      u.uTexel.value.set(1 / hw, 1 / hh);
      u.uResolution.value.set(hw, hh);
      u.uNoiseScale.value.set(hw / noiseSize, hh / noiseSize);
      u.uRadius.value = p.radius;
      u.uIntensity.value = p.intensity;
      u.uPower.value = p.power;
      u.uProj11.value = p.proj11;
      u.uFrame.value = p.frame % 64;
      this.blit.draw(renderer, this.matAO, this.halfRT);

      const b = this.matBlur.uniforms;
      b.tAO.value = this.halfRT.texture;
      b.uDir.value.set(1 / hw, 0);
      this.blit.draw(renderer, this.matBlur, this.blurRT);
      b.tAO.value = this.blurRT.texture;
      b.uDir.value.set(0, 1 / hh);
      this.blit.draw(renderer, this.matBlur, this.halfRT);
    }

    const r = this.matUp.uniforms;
    r.tAO.value = this.halfRT.texture;
    r.tDepth.value = p.depthTexture;
    r.tNormal.value = p.normalTexture;
    r.tNoise.value = p.noiseTexture;
    r.uInvProj.value.copy(p.invProj);
    r.uHalfTexel.value.set(1 / hw, 1 / hh);
    r.uNoiseScale.value.set(this.w / noiseSize, this.h / noiseSize);
    r.uAoEnabled.value = doAO ? 1 : 0;
    r.uFrame.value = p.frame % 64;

    const c = p.contact;
    if (c && c.strength > 0) {
      r.uProj.value.copy(c.proj);
      r.uLightVS.value.copy(c.lightVS);
      r.uCsStrength.value = c.strength;
      r.uCsLength.value = c.length;
      r.uCsThickness.value = c.thickness;
      r.uCsBias.value = c.bias;
      r.uCsFadeStart.value = c.fadeStart;
      r.uCsFadeEnd.value = c.fadeEnd;
    } else {
      r.uCsStrength.value = 0;
    }
    this.blit.draw(renderer, this.matUp, target || this.fullRT);
  }

  dispose() {
    for (const rt of [this.halfRT, this.blurRT, this.fullRT, this.vmRT]) if (rt) rt.dispose();
    this.matAO.dispose(); this.matBlur.dispose(); this.matUp.dispose();
  }
}
